import { useState, useCallback } from "react";
import { Plus, Trash2, ChevronDown, ChevronRight, GitBranch, CornerDownRight, Pencil } from "lucide-react";

interface SwitchCondition {
  field: string;
  operator: string;
  value?: unknown;
}

interface SwitchCase {
  key: string;
  name: string;
  match: "All" | "Any";
  conditions: SwitchCondition[];
}

interface SwitchNodeEditorProps {
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}

const OPERATORS: { value: string; label: string }[] = [
  { value: "Equals", label: "es igual a" },
  { value: "NotEquals", label: "no es igual a" },
  { value: "GreaterThan", label: "es mayor que" },
  { value: "GreaterThanOrEqual", label: "es mayor o igual que" },
  { value: "LessThan", label: "es menor que" },
  { value: "LessThanOrEqual", label: "es menor o igual que" },
  { value: "Contains", label: "contiene" },
  { value: "NotContains", label: "no contiene" },
  { value: "StartsWith", label: "comienza con" },
  { value: "EndsWith", label: "termina con" },
  { value: "IsEmpty", label: "está vacío" },
  { value: "IsNotEmpty", label: "no está vacío" },
  { value: "In", label: "está en la lista" },
  { value: "NotIn", label: "no está en la lista" },
];

const UNARY_OPERATORS = new Set(["IsEmpty", "IsNotEmpty"]);
const LIST_OPERATORS = new Set(["In", "NotIn"]);

const MAX_CASES = 10;

// Colores por ruta — cada ruta produce una salida visualmente distinta, igual que sus edges.
const ROUTE_COLORS = [
  "#0071e3", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899",
  "#06b6d4", "#ef4444", "#84cc16", "#f97316", "#6366f1",
];

const inputCls =
  "w-full bg-white border border-black/[0.07] rounded-lg py-2 px-2.5 text-[13px] font-medium text-foreground placeholder:text-foreground/25 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/10 transition-all";
const selectCls = `${inputCls} cursor-pointer appearance-none pr-7 bg-no-repeat`;

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

function ensureUniqueKey(base: string, cases: SwitchCase[], selfIndex: number): string {
  const fallback = base || "ruta";
  const taken = new Set(cases.filter((_, i) => i !== selfIndex).map((c) => c.key));
  if (!taken.has(fallback)) return fallback;
  let i = 2;
  while (taken.has(`${fallback}_${i}`)) i++;
  return `${fallback}_${i}`;
}

function parseCases(value: Record<string, unknown>): SwitchCase[] {
  const raw = value.cases;
  if (!Array.isArray(raw)) return [];
  return raw.map((c: unknown) => {
    const obj = c as Record<string, unknown>;
    return {
      key: String(obj.key ?? ""),
      name: String(obj.name ?? ""),
      match: obj.match === "Any" ? "Any" : "All",
      conditions: Array.isArray(obj.conditions)
        ? obj.conditions.map((cond: unknown) => {
            const co = cond as Record<string, unknown>;
            return {
              field: String(co.field ?? ""),
              operator: String(co.operator ?? "Equals"),
              value: co.value,
            };
          })
        : [],
    };
  });
}

function ConditionRow({
  cond,
  canRemove,
  onChange,
  onRemove,
}: {
  cond: SwitchCondition;
  canRemove: boolean;
  onChange: (patch: Partial<SwitchCondition>) => void;
  onRemove: () => void;
}) {
  const isUnary = UNARY_OPERATORS.has(cond.operator);
  const isList = LIST_OPERATORS.has(cond.operator);

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-1.5">
        <input
          value={cond.field}
          onChange={(e) => onChange({ field: e.target.value })}
          placeholder="body.tier"
          className={`${inputCls} font-mono`}
          spellCheck={false}
        />
        <div className="relative">
          <select
            value={cond.operator}
            onChange={(e) =>
              onChange({
                operator: e.target.value,
                ...(UNARY_OPERATORS.has(e.target.value) ? { value: undefined } : {}),
              })
            }
            className={selectCls}
          >
            {OPERATORS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown className="size-3.5 text-foreground/30 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {!isUnary && (
          <input
            value={cond.value === undefined || cond.value === null ? "" : String(cond.value)}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder={isList ? "gold, silver, bronze" : "gold"}
            className={`${inputCls} col-span-2`}
            spellCheck={false}
          />
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        className="p-1.5 rounded-lg text-foreground/25 hover:bg-red-500/10 hover:text-red-500 transition-colors shrink-0 disabled:opacity-0 disabled:pointer-events-none"
        aria-label="Eliminar condición"
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  );
}

export default function SwitchNodeEditor({ value, onChange }: SwitchNodeEditorProps) {
  const cases = parseCases(value);
  const defaultCaseKey = String(value.defaultCaseKey ?? "default");
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  const [editingKey, setEditingKey] = useState<number | null>(null);

  const emit = useCallback(
    (nextCases: SwitchCase[], nextDefault?: string) => {
      onChange({
        ...value,
        cases: nextCases,
        defaultCaseKey: nextDefault ?? defaultCaseKey,
      });
    },
    [value, onChange, defaultCaseKey],
  );

  const toggleCollapse = (index: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const updateCase = (index: number, patch: Partial<SwitchCase>) => {
    emit(cases.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  // El nombre amigable genera el id de salida (slug). Mientras el id no se edite
  // manualmente, se mantiene sincronizado con el nombre para evitar dos campos a gestionar.
  const handleNameChange = (index: number, name: string) => {
    const c = cases[index];
    const keyWasAuto = c.key === "" || c.key === slugify(c.name);
    const patch: Partial<SwitchCase> = { name };
    if (keyWasAuto) {
      const slug = slugify(name);
      patch.key = slug ? ensureUniqueKey(slug, cases, index) : c.key;
    }
    updateCase(index, patch);
  };

  const handleKeyChange = (index: number, raw: string) => {
    updateCase(index, { key: ensureUniqueKey(slugify(raw), cases, index) });
  };

  const addCase = () => {
    if (cases.length >= MAX_CASES) return;
    const name = `Ruta ${cases.length + 1}`;
    const key = ensureUniqueKey(slugify(name), cases, -1);
    emit([
      ...cases,
      { key, name, match: "All", conditions: [{ field: "", operator: "Equals", value: "" }] },
    ]);
  };

  const removeCase = (index: number) => {
    emit(cases.filter((_, i) => i !== index));
    setEditingKey(null);
  };

  const updateCondition = (caseIndex: number, condIndex: number, patch: Partial<SwitchCondition>) => {
    const c = cases[caseIndex];
    updateCase(caseIndex, {
      conditions: c.conditions.map((cond, i) => (i === condIndex ? { ...cond, ...patch } : cond)),
    });
  };

  const addCondition = (caseIndex: number) => {
    const c = cases[caseIndex];
    updateCase(caseIndex, {
      conditions: [...c.conditions, { field: "", operator: "Equals", value: "" }],
    });
  };

  const removeCondition = (caseIndex: number, condIndex: number) => {
    const c = cases[caseIndex];
    updateCase(caseIndex, { conditions: c.conditions.filter((_, i) => i !== condIndex) });
  };

  return (
    <div className="space-y-3">
      {/* Explicación */}
      <div className="flex gap-2.5 p-3 rounded-xl bg-accent/[0.04] border border-accent/10">
        <GitBranch className="size-4 text-accent shrink-0 mt-0.5" />
        <p className="text-[11px] leading-relaxed text-foreground/55">
          Evalúa las rutas <strong className="font-semibold text-foreground/70">de arriba a abajo</strong> y
          ejecuta la primera que coincida. Conecta cada ruta a un nodo usando su salida.
        </p>
      </div>

      {/* Empty state */}
      {cases.length === 0 && (
        <div className="text-center py-6 px-4 rounded-xl border border-dashed border-black/10">
          <p className="text-[13px] font-semibold text-foreground/60">Sin rutas todavía</p>
          <p className="text-[11px] text-foreground/35 mt-0.5">Agrega una ruta para empezar a clasificar.</p>
        </div>
      )}

      {/* Rutas */}
      {cases.map((caseItem, caseIndex) => {
        const isCollapsed = collapsed.has(caseIndex);
        const color = ROUTE_COLORS[caseIndex % ROUTE_COLORS.length];
        const condCount = caseItem.conditions.length;
        const isEditingThisKey = editingKey === caseIndex;

        return (
          <div
            key={caseIndex}
            className="rounded-xl bg-white/70 border border-black/[0.06] overflow-hidden shadow-sm"
            style={{ borderLeft: `3px solid ${color}` }}
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button
                type="button"
                onClick={() => toggleCollapse(caseIndex)}
                className="p-0.5 -ml-0.5 rounded text-foreground/40 hover:text-foreground/70 transition-colors shrink-0"
                aria-label={isCollapsed ? "Expandir" : "Contraer"}
              >
                {isCollapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
              </button>

              <input
                value={caseItem.name}
                onChange={(e) => handleNameChange(caseIndex, e.target.value)}
                placeholder="Nombre de la ruta"
                className="flex-1 min-w-0 bg-transparent text-[13px] font-semibold text-foreground placeholder:text-foreground/30 focus:outline-none"
                spellCheck={false}
              />

              {isCollapsed && (
                <span className="text-[10px] text-foreground/30 shrink-0">
                  {condCount} {condCount === 1 ? "condición" : "condiciones"}
                </span>
              )}

              <button
                type="button"
                onClick={() => removeCase(caseIndex)}
                className="p-1.5 rounded-lg text-foreground/25 hover:bg-red-500/10 hover:text-red-500 transition-colors shrink-0"
                aria-label="Eliminar ruta"
              >
                <Trash2 className="size-3.5" />
              </button>
            </div>

            {!isCollapsed && (
              <div className="px-3 pb-3 space-y-3">
                {/* Id de salida */}
                <div className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-foreground/35 font-medium uppercase tracking-wide">Salida</span>
                  {isEditingThisKey ? (
                    <input
                      autoFocus
                      value={caseItem.key}
                      onChange={(e) => handleKeyChange(caseIndex, e.target.value)}
                      onBlur={() => setEditingKey(null)}
                      onKeyDown={(e) => e.key === "Enter" && setEditingKey(null)}
                      className="font-mono text-[11px] bg-white border border-accent/40 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-accent/10 w-40"
                      spellCheck={false}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingKey(caseIndex)}
                      className="group inline-flex items-center gap-1 font-mono text-[11px] text-foreground/55 bg-black/[0.04] hover:bg-black/[0.07] rounded px-1.5 py-0.5 transition-colors"
                      title="Identificador usado al conectar esta ruta. Click para editar."
                    >
                      {caseItem.key || "—"}
                      <Pencil className="size-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                    </button>
                  )}
                </div>

                {/* Match mode — solo relevante con >1 condición */}
                {condCount > 1 && (
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-foreground/45">Se cumplen</span>
                    <div className="inline-flex rounded-lg border border-black/[0.07] p-0.5 bg-black/[0.02]">
                      {(["All", "Any"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => updateCase(caseIndex, { match: mode })}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                            caseItem.match === mode
                              ? "bg-white text-foreground shadow-sm"
                              : "text-foreground/40 hover:text-foreground/70"
                          }`}
                        >
                          {mode === "All" ? "todas" : "alguna"}
                        </button>
                      ))}
                    </div>
                    <span className="text-foreground/45">de:</span>
                  </div>
                )}

                {/* Condiciones */}
                <div className="space-y-1.5">
                  {caseItem.conditions.map((cond, condIndex) => (
                    <ConditionRow
                      key={condIndex}
                      cond={cond}
                      canRemove={condCount > 1}
                      onChange={(patch) => updateCondition(caseIndex, condIndex, patch)}
                      onRemove={() => removeCondition(caseIndex, condIndex)}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => addCondition(caseIndex)}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-accent hover:opacity-75 transition-opacity"
                >
                  <Plus className="size-3.5" />
                  Agregar condición
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Agregar ruta */}
      <button
        type="button"
        onClick={addCase}
        disabled={cases.length >= MAX_CASES}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-accent/30 text-[13px] font-semibold text-accent hover:bg-accent/[0.04] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        <Plus className="size-4" />
        {cases.length >= MAX_CASES ? `Máximo ${MAX_CASES} rutas` : "Agregar ruta"}
      </button>

      {/* Ruta por defecto (else) */}
      <div className="rounded-xl bg-black/[0.02] border border-black/[0.06] overflow-hidden" style={{ borderLeft: "3px solid #9ca3af" }}>
        <div className="px-3 py-2.5 flex items-start gap-2">
          <CornerDownRight className="size-4 text-foreground/30 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-foreground/70">Por defecto</p>
            <p className="text-[10.5px] text-foreground/40 leading-snug mt-0.5">
              Se ejecuta cuando ninguna ruta anterior coincide.
            </p>
            <div className="flex items-center gap-1.5 text-[10px] mt-2">
              <span className="text-foreground/35 font-medium uppercase tracking-wide">Salida</span>
              <input
                value={defaultCaseKey}
                onChange={(e) => emit(cases, slugify(e.target.value) || "default")}
                className="font-mono text-[11px] text-foreground/55 bg-white border border-black/[0.07] rounded px-1.5 py-0.5 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/10 w-40"
                spellCheck={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
